/**
 * @ComponentFor MarketsPageViewModel
 */
import connect from 'Shared/connect';
import React from 'react';
import BlockPropType from './MarketsPageViewModel.type';
import { styled } from '@glitz/react';
import * as style from 'Shared/Style';
import countryLinkBlockViewModelType from 'Shared/Blocks/CountryLink/CountryLinkBlockViewModel.type';
import CountryLinkBlock from 'Shared/Blocks/CountryLink/CountryLinkBlock';
import Ul from 'Shared/Generic/Ul';
import { H1, Paragraph, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { isCompact } from 'Shared/Viewport';
import Page from 'Shared/Page';
import Image, { Ratio as ImageRatio } from 'Shared/Image/Ratio';
import { epiPropertyValue } from '@polarnopyret/scope-episerver';
import { createGridClasses } from 'Shared/grid';
import { AltLogo } from 'Shared/Logotype';

type ConnectedProps = {
  currentBreakpoint: number;
};

type Props = BlockPropType & ConnectedProps;

const Container = styled.div({
  marginTop: '16px',
});

const StyledP = styled(Paragraph, {
  display: 'inline-block',
});

const Item = styled.li({
  padding: { y: '3px' }
});

const LogoWrap = styled.div({
  marginTop: '48px',
  marginBottom: '48px',
})

const Logo = styled(AltLogo, {
  width: 'auto',
  height: '32px',
});

const CountrySelectionWrapper = styled.div({
  marginRight: '12px',
  marginLeft: '12px',
  ['@media ' + style.mediaMinLarge]: {
    marginTop: '32px',
    marginRight: '48px',
    marginLeft: '48px',
  },
});

const createList = (list: any) => {
  return (
    <Ul bare>
      {list.map((item: countryLinkBlockViewModelType, i: number) => {
        return (
          <Item key={i}>
            <CountryLinkBlock {...item} />
          </Item>
        );
      })}
    </Ul>
  );
};

const createSuggestion = (
  list: any[],
  countryCode: string,
  suggestionText: string,
) => {
  const matchingCountryLinkItem = list.find((item: any) => {
    const countryLinkItem = item as countryLinkBlockViewModelType;
    return countryLinkItem.countryCode === countryCode;
  });
  if (matchingCountryLinkItem) {
    return (
      <Container>
        <StyledP>{suggestionText}: </StyledP>
        <CountryLinkBlock {...matchingCountryLinkItem} />
      </Container>
    );
  }
  return undefined;
};

function MarketsPage(props: Props) {
  const page = props.page;

  // copy array and sort it
  const items = []
    .concat(epiPropertyValue(page.countryLinks).items)
    .sort((a: countryLinkBlockViewModelType, b: countryLinkBlockViewModelType) => {
      if (a.englishName === b.englishName) {
        return 0;
      }
      return a.englishName > b.englishName ? 1 : -1;
    });

  const halfListLength = Math.ceil(items.length * 0.5);
  const leftList = items.slice(0, halfListLength);
  const rightList = items.slice(halfListLength, items.length);
  const isMobile = isCompact(props.currentBreakpoint);

  return (
    <Page>
      <div className='grid'>
        <LogoWrap className={createGridClasses(12, 12)}>
          <Logo />
        </LogoWrap>
        {!isMobile && (
          <div className={createGridClasses(12, 6)}>
            <Image
              src={epiPropertyValue(page.image)}
              title={'polarnopyret.com'}
              alt={'polarnopyret.com'}
              ratio={ImageRatio.OneToOne}
            />
          </div>
        )}
        <div className={createGridClasses(12, 6)}>
          <CountrySelectionWrapper>
            <H1 fontSize={ResponsiveSize.D32_M20} upperCase={false}>
              {epiPropertyValue(page.header) ? epiPropertyValue(page.header) : props.labels.title}
            </H1>
            {createSuggestion(items, props.ipCountryCode, props.labels.suggestion)}
            <Container className='grid'>
              <div className={createGridClasses(12, 6)}>
                {createList(leftList)}
              </div>
              <div className={createGridClasses(12, 6)}>
                {createList(rightList)}
              </div>
            </Container>
          </CountrySelectionWrapper>
        </div>
      </div>
    </Page>
  );
}

export default connect(state => ({
  currentBreakpoint: state.currentBreakpoint,
}))(MarketsPage);
